import React, { useState, useEffect, useRef, CSSProperties } from 'react';
import {
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Box,
} from '@mui/material';
import { useDebounce } from '../../hooks/useDebounce';
import { useGoogleLocations } from '../../hooks/useFetchGooglePlaces';

interface Suggestion {
  name: string;
  address: string;
  placeId?: string;
}

interface LocationAutocompleteProps {
  value?: string;
  onChange?: (location: string) => void;
  onPlaceSelected?: (place: { address: string; placeId: string }) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  width?: string;
  inputStyle?: CSSProperties;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value = '',
  onChange,
  onPlaceSelected,
  placeholder = 'Enter Business Location',
  error = false,
  helperText = '',
  width = '100%',
  inputStyle = {},
}) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSelectionMade, setIsSelectionMade] = useState<boolean>(!!value);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Debounce the input for optimized API calls
  const debouncedInput = useDebounce(inputValue, 300);

  // Fetch locations on input change (if input is non-empty and no selection made)
  const { data, isLoading } = useGoogleLocations(
    !isSelectionMade && debouncedInput ? debouncedInput : ''
  );

  // Sync internal state with external value
  useEffect(() => {
    if (value && value !== inputValue) {
      setInputValue(value);
      setIsSelectionMade(true); // Prevent fetching when value is provided externally
    }
  }, [value, inputValue]);

  // Update suggestions when data changes
  useEffect(() => {
    if (data && Array.isArray(data.data)) {
      setSuggestions(
        data.data.map((place: any) => ({
          name: place.name,
          address: place.formatted_address,
          placeId: place.place_id,
        }))
      );
    } else {
      setSuggestions([]);
    }
  }, [data]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsSelectionMade(false); // Enable fetching on every change
    onChange?.(newValue); // Notify parent component on change
  };

  // Handle selecting a location
  const handleSelectLocation = (suggestion: Suggestion) => {
    setInputValue(suggestion.address);
    onChange?.(suggestion.address);

    // Call onPlaceSelected callback if provided and place_id exists
    if (onPlaceSelected && suggestion.placeId) {
      onPlaceSelected({
        address: suggestion.address,
        placeId: suggestion.placeId,
      });
    }

    setSuggestions([]);
    setIsSelectionMade(true); // Prevent further fetching after selection
  };

  // Close suggestions on clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box ref={wrapperRef} sx={{ width: '100%', position: 'relative' }}>
      <TextField
        fullWidth
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        sx={{
          width: width,
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            bgcolor: 'white',
            ...inputStyle,
          },
        }}
        size="small"
        error={error}
        helperText={helperText}
        InputProps={{
          endAdornment: isLoading ? <CircularProgress size={20} /> : null,
        }}
      />

      {suggestions.length > 0 && (
        <List
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            bgcolor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1400,
            background: '#fdfdfd',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            mt: 0.5,
          }}
        >
          {suggestions.map((suggestion, index) => (
            <ListItem
              key={index}
              component="li"
              onClick={() => handleSelectLocation(suggestion)}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText
                primary={suggestion.name}
                secondary={suggestion.address}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default LocationAutocomplete;
